import { useEffect } from 'react';

import { useAtomValue } from 'jotai';

import { filtersListAtom } from '@core/Atoms/Plp/FiltersListAtom';
import type { Tenant } from '@core/Entities/App/Tenants.entity';
import type { VariantAtom } from '@core/Entities/Search/Atoms/AvailableVariantsAtom.entity';
import type { Item } from '@plp/Components/MultiSelect/MultiSelect.entity';
import { getArrayFilterValue } from '@plp/Utils/Filters/GetArrayFilterValue';
import type { Logos } from '@search-filters/Components/MakeLabel/Logos';
import MakeLabel from '@search-filters/Components/MakeLabel/MakeLabel';
import { useFilterLink } from '@search-filters/Hooks/UseFilterLink/UseFilterLink';
import { useSearchFilter } from '@search-filters/Hooks/UseSearchFilter';
import { useUpdateFilters } from '@search-filters/Hooks/UseUpdateFilters';

import { type Model, normalizedModelAtom } from '../../../../Atoms/NormalizedMakesAtom';
import { FilterLink } from '../FilterLink/FilterLink';

const mapVariants = (
  availableVariantsAtom: VariantAtom[],
  models: Record<string, Model>,
  getLinkForFilterValue: ReturnType<typeof useFilterLink>['getLinkForFilterValue'],
): Item[] => {
  if (Object.keys(models).length === 0) return [];

  return availableVariantsAtom.map(variant => {
    const maybeLink = getLinkForFilterValue({
      filterValue: variant.key,
      resultCount: variant.count,
      isOnlyOption: availableVariantsAtom.length === 1,
    });

    return {
      id: variant.key,
      renderLabel: (
        <MakeLabel
          count={variant.count}
          make={variant.make as Logos}
          name={variant.displayName}
          nameRender={<FilterLink link={maybeLink}>{variant.displayName}</FilterLink>}
        />
      ),
      label: variant.displayName,
      model: `${variant.modelDisplayName} variants`,
    };
  });
};

const useModelVariant = (isPlpFiltersSeoEnabled: boolean, tenant: Tenant | undefined) => {
  const FILTER_ID = 'variant-type';
  const filterList = useAtomValue(filtersListAtom);
  const normalizedModels = useAtomValue(normalizedModelAtom);
  const activeModels = getArrayFilterValue('model', filterList) || [];
  const activeModelVariants = getArrayFilterValue(FILTER_ID, filterList) || [];
  const { updateFilters } = useUpdateFilters();
  const { getLinkForFilterValue } = useFilterLink({
    filterId: FILTER_ID,
    isPlpFiltersSeoEnabled,
    tenant,
  });

  useEffect(() => {
    const disallowedVariants = activeModelVariants.filter(variantId => {
      const variantIdParts = variantId.split('-');
      return !activeModels.includes(`${variantIdParts[0]}-${variantIdParts[1]}`);
    });

    if (disallowedVariants.length > 0) {
      const allowedVariants = activeModelVariants.filter(
        variantId => !disallowedVariants.includes(variantId),
      );
      updateFilters([{ id: FILTER_ID, value: allowedVariants }]);
    }
  }, [activeModels]);

  const variants = activeModels.reduce((acc, models) => {
    const variants = normalizedModels[models]?.trimsPerModel ?? [];
    return [...acc, ...variants];
  }, [] as VariantAtom[]);

  const allVariants = mapVariants(variants, normalizedModels, getLinkForFilterValue);

  return useSearchFilter({ filterId: FILTER_ID, data: allVariants });
};

export default useModelVariant;
